// src/contexts/AuthContext.js - Updated with enhanced authentication
import React, { createContext, useContext, useState, useEffect } from 'react';
import { createLogger } from '../utils/logger';
import { setSentryUser } from '../utils/sentry';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from 'firebase/auth';
import {
  setDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { PaymentService } from '../services/paymentService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const logger = createLogger('AuthContext');

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);

  useEffect(() => {
    // Add network status monitoring
    const handleOnline = () => {
      logger.info('Network status: ONLINE');
      setNetworkStatus(true);
    };

    const handleOffline = () => {
      logger.info('Network status: OFFLINE');
      setNetworkStatus(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const setupAuthPersistence = async () => {
      try {
        // Check if local storage is available
        let storageAvailable = true;
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
        } catch (e) {
          storageAvailable = false;
        }

        // If local storage is available and the user is online, use local persistence
        // Otherwise fall back to session or in-memory persistence
        if (storageAvailable && networkStatus) {
          await setPersistence(auth, browserLocalPersistence);
          logger.debug('Auth persistence set to LOCAL');
        } else if (storageAvailable) {
          await setPersistence(auth, browserSessionPersistence);
          logger.debug('Auth persistence set to SESSION');
        } else {
          await setPersistence(auth, inMemoryPersistence);
          logger.debug('Auth persistence set to IN-MEMORY');
        }
      } catch (error) {
        logger.error('Error setting auth persistence:', error);
        try {
          // Fall back to in-memory persistence if other methods fail
          await setPersistence(auth, inMemoryPersistence);
          logger.debug('Fallback: Auth persistence set to IN-MEMORY');
        } catch (fallbackError) {
          logger.error(
            'Critical error setting auth persistence:',
            fallbackError
          );
        }
      }
    };
    setupAuthPersistence();
  }, [networkStatus]);

  async function signup(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      await updateProfile(userCredential.user, { displayName });
      await createUserProfile(userCredential.user, { displayName });
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();

    // Log the current domain
    logger.debug('Google Sign-in attempt from:', window.location.hostname);

    // Force account selection
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    try {
      // First try with popup
      try {
        const result = await signInWithPopup(auth, provider);
        const isNewUser = result._tokenResponse?.isNewUser;
        if (isNewUser) {
          await createUserProfile(result.user, {
            displayName: result.user.displayName,
          });
        }
        return result.user;
      } catch (popupError) {
        logger.error('Google popup error details:', {
          code: popupError.code,
          message: popupError.message,
          customData: popupError.customData,
          domain: window.location.hostname,
        });

        // Check if the error is related to popup being blocked or network issues
        if (
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/network-request-failed' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/unauthorized-domain'
        ) {
          logger.debug(
            'Popup authentication failed, falling back to redirect method:',
            popupError.code
          );
          // Use redirect method as fallback
          const redirectResult = await signInWithRedirect(auth, provider);
          return redirectResult.user;
        } else {
          // For other errors, rethrow
          throw popupError;
        }
      }
    } catch (error) {
      logger.error('Google authentication error:', error.code, error.message);

      // Check if in-app browser
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const isInAppBrowser =
        ua.indexOf('FBAN') > -1 ||
        ua.indexOf('FBAV') > -1 ||
        ua.indexOf('Instagram') > -1 ||
        ua.indexOf('LinkedInApp') > -1;

      // Transform network-related errors into more user-friendly messages
      if (error.code === 'auth/network-request-failed') {
        if (isInAppBrowser) {
          throw new Error(
            'Sign-in is not supported in this browser. Please open this page in Chrome, Safari, or Firefox to continue.'
          );
        }
        throw new Error(
          'Network connection issue. Please check your internet connection and try again.'
        );
      } else if (
        error.code === 'auth/web-storage-unsupported' ||
        error.code === 'auth/unauthorized-domain'
      ) {
        throw new Error(
          "This browser doesn't support secure sign-in. Please open this page in Chrome, Safari, or Firefox."
        );
      } else if (error.code === 'auth/timeout') {
        throw new Error(
          'Authentication timed out. Please try again on a more stable network connection.'
        );
      } else {
        throw error;
      }
    }
  }

  async function signInWithApple() {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    try {
      // First try with popup
      try {
        const result = await signInWithPopup(auth, provider);
        const isNewUser = result._tokenResponse?.isNewUser;
        if (isNewUser) {
          await createUserProfile(result.user, {
            displayName: result.user.displayName || 'Apple User',
          });
        }
        return result.user;
      } catch (popupError) {
        // Check if the error is related to popup being blocked or network issues
        if (
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/network-request-failed' ||
          popupError.code === 'auth/popup-closed-by-user'
        ) {
          logger.debug(
            'Apple popup authentication failed, falling back to redirect method:',
            popupError.code
          );
          // Use redirect method as fallback
          const redirectResult = await signInWithRedirect(auth, provider);
          return redirectResult.user;
        } else {
          // For other errors, rethrow
          throw popupError;
        }
      }
    } catch (error) {
      logger.error('Apple authentication error:', error.code, error.message);
      // Transform network-related errors into more user-friendly messages
      if (error.code === 'auth/network-request-failed') {
        throw new Error(
          'Network connection issue. Please check your internet connection and try again.'
        );
      } else if (error.code === 'auth/web-storage-unsupported') {
        throw new Error(
          'Your browser or network may be blocking storage access. Try disabling private browsing or security restrictions.'
        );
      } else if (error.code === 'auth/timeout') {
        throw new Error(
          'Authentication timed out. Please try again on a more stable network connection.'
        );
      } else {
        throw error;
      }
    }
  }

  async function createUserProfile(user, additionalData = {}) {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) {
      const { email, displayName, photoURL } = user;
      try {
        await setDoc(userRef, {
          email,
          displayName: displayName || additionalData.displayName,
          photoURL: photoURL || null,
          createdAt: serverTimestamp(),
          quizAttempts: 0,
          hasPurchasedReport: false,
          ...additionalData,
        });
      } catch (error) {
        logger.error('Error creating user profile', error);
      }
    }
    return getDoc(userRef);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function getUserProfile(uid) {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      return null;
    }
  }

  async function checkPurchaseStatus(userId = currentUser?.uid) {
    if (!userId) return false;
    logger.debug('🔍 Checking purchase status for user:', userId);
    const result = await PaymentService.checkUserPurchaseStatus(userId);
    logger.debug('📊 Purchase status result:', result);
    return result;
  }

  /**
   * Start a Stripe Checkout session.
   * Accepts a tier id (server validates the price + product name).
   * If the user is not authenticated, guestEmail is required — the webhook
   * will create a Firebase Auth user and email them a sign-in link.
   *
   * @param {Object} params
   * @param {'tip'|'deep'|'couple'} params.tier
   * @param {string} [params.guestEmail]  Required if not authenticated
   * @param {string} [params.successUrl]  Defaults to /payment-success
   * @param {string} [params.cancelUrl]   Defaults to /payment-cancel
   * @param {string} [params.compareId]   Optional — pairs a couple report to a comparison
   */
  async function processPurchase(params) {
    const { tier, guestEmail, successUrl, cancelUrl, compareId } = params || {};

    if (!tier) throw new Error('tier is required');
    if (!currentUser && !guestEmail) {
      throw new Error(
        'Either an authenticated session or guestEmail is required.'
      );
    }

    const defaultSuccessUrl = `${window.location.origin}/payment-success`;
    const defaultCancelUrl = `${window.location.origin}/payment-cancel`;

    try {
      const createStripeCheckout = httpsCallable(
        functions,
        'createStripeCheckout'
      );
      const result = await createStripeCheckout({
        tier,
        guestEmail: currentUser ? undefined : guestEmail,
        compareId: compareId || undefined,
        successUrl: successUrl || defaultSuccessUrl,
        cancelUrl: cancelUrl || defaultCancelUrl,
      });
      return result.data;
    } catch (error) {
      logger.error('Stripe checkout error:', error);
      throw error;
    }
  }

  async function saveQuizResults(results) {
    if (!currentUser) throw new Error('User must be logged in to save results');
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        quizResults: results,
        lastQuizDate: serverTimestamp(),
        quizAttempts: increment(1),
      });
      const researchRef = doc(
        db,
        'quizresearch',
        currentUser.uid + '_' + new Date().toISOString()
      );
      await setDoc(researchRef, {
        userId: currentUser.uid,
        timestamp: serverTimestamp(),
        rawAnswers: results.rawAnswers || null,
        demographics: results.demographics || null,
        scores: results.userScores || null,
      });
    } catch (error) {
      logger.error('Error saving quiz results:', error);
      throw error;
    }
  }

  async function getSavedQuizResults() {
    if (!currentUser) return null;
    return PaymentService.getSavedQuizResults(currentUser.uid);
  }

  useEffect(() => {
    // Check for redirect result when the component mounts
    // This handles the case when a user returns to the app after redirect auth
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          logger.debug('🔑 User authenticated via redirect:', result.user.uid);
          const isNewUser = result._tokenResponse?.isNewUser;
          if (isNewUser) {
            await createUserProfile(result.user, {
              displayName: result.user.displayName,
            });
          }
        }
      } catch (error) {
        logger.error('❌ Error processing redirect result:', error);
        // Add specific handling for network errors during redirect
        if (error.code === 'auth/network-request-failed') {
          logger.debug(
            '🔄 Network issue during redirect authentication, will retry when back online'
          );
          // Could implement retry logic here using a timeout or when network is back
        }
      }
    };

    checkRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async user => {
      logger.debug(
        '🔑 Auth state changed:',
        user ? `User ${user.uid} logged in` : 'User logged out'
      );
      setCurrentUser(user);
      if (user) {
        // Set user context in Sentry for error tracking
        setSentryUser(user);

        try {
          if (networkStatus) {
            // Only attempt token refresh if online
            await user.getIdToken(true);
            logger.debug(
              '✅ Authentication token refreshed on auth state change'
            );
          } else {
            logger.debug('⚠️ Network offline, skipping token refresh');
          }
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);
        } catch (error) {
          logger.error('❌ Error during auth state change:', error);
          // Add retry logic for network errors
          if (error.code === 'auth/network-request-failed') {
            logger.debug(
              '⚠️ Network error during token refresh, will use cached auth state'
            );
          }
        }
      } else {
        setUserProfile(null);
        // Clear user context in Sentry
        setSentryUser(null);
        // Clear quiz-related localStorage so stale results don't leak to new accounts
        localStorage.removeItem('wwyd_results');
        localStorage.removeItem('wwyd_share_id');
        localStorage.removeItem('wwyd_share_cluster');
        localStorage.removeItem('wwyd_compare_results');
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [networkStatus]);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    signInWithGoogle,
    signInWithApple,
    logout,
    resetPassword,
    loading,
    networkStatus,
    checkPurchaseStatus,
    processPurchase,
    saveQuizResults,
    getSavedQuizResults,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
